import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Search, Wallet, Loader2, Plus, Minus, X, Euro, ChevronRight,
  User as UserIcon, Car, Filter, ArrowUpRight, ArrowDownLeft, Clock,
} from "lucide-react";

/**
 * Admin — Wallets Tab.
 *
 * Vue globale des portefeuilles :
 *   - Liste tous les covoitureurs (drivers.wallet_balance_eur) + tous les
 *     passagers (clients.referral_credit_balance).
 *   - Recherche par email/nom, filtre par rôle.
 *   - Cliquer sur un user ouvre un modal : solde, historique ledger,
 *     boutons + / - pour crédit/débit rapide avec note.
 *
 * Endpoints :
 *   - GET /api/admin/price/wallets?q=&role=&limit=
 *   - POST /api/admin/price/users/{user_id}/wallet/adjust { amount_eur, note }
 *   - GET /api/admin/price/users/{user_id}/wallet/ledger
 */

function fmtEur(n) {
  return `${Number(n || 0).toFixed(2).replace(".", ",")} €`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }) +
      " · " + d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function WalletRow({ item, onOpen }) {
  const Icon = item.role === "driver" ? Car : UserIcon;
  const isDriver = item.role === "driver";
  return (
    <button
      type="button"
      onClick={() => onOpen(item)}
      className="w-full flex items-center gap-3 rounded-2xl bg-white p-3 street-border-thin street-shadow-sm active:translate-y-[1px] transition-transform"
      data-testid={`wallet-row-${item.user_id?.slice(0, 8)}`}
    >
      <div className={
        "h-10 w-10 flex-none rounded-xl grid place-items-center street-border-thin " +
        (isDriver ? "bg-[#FF3B3B] text-white" : "bg-[#DCE9FF] text-[#0F2634]")
      }>
        <Icon className="h-4 w-4" strokeWidth={3} />
      </div>
      <div className="flex-1 min-w-0 text-left">
        <div className="font-[Outfit] text-[13.5px] font-black leading-tight truncate">
          {item.name || item.email || "—"}
        </div>
        <div className="text-[11px] font-medium text-foreground/60 truncate">
          {item.email} · {isDriver ? "Covoitureur" : "Passager"}
        </div>
        {isDriver && (
          <div className="mt-1 flex items-center gap-1.5">
            <span className="text-[10px] font-black text-primary bg-primary/10 px-1.5 py-0.5 rounded-full">
              🐷 {fmtEur(item.cagnotte_balance_eur)}
            </span>
            <span className="text-[10px] font-black text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-full">
              💳 {fmtEur(item.wallet_balance_eur)}
            </span>
          </div>
        )}
      </div>
      {!isDriver && (
        <div className="text-right">
          <div className="font-[Outfit] text-[15px] font-black tabular-nums">
            {fmtEur(item.balance_eur)}
          </div>
          <div className="text-[9px] font-bold text-foreground/50 uppercase tracking-widest">
            Crédit
          </div>
        </div>
      )}
      <ChevronRight className="h-4 w-4 flex-none text-foreground/40" strokeWidth={3} />
    </button>
  );
}

function WalletDetailModal({ item, onClose, onUpdated }) {
  const isDriver = item.role === "driver";
  const [ledger, setLedger] = useState([]);
  const [loadingLedger, setLoadingLedger] = useState(true);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [balanceKind, setBalanceKind] = useState("cagnotte"); // "cagnotte" | "wallet"
  const [busy, setBusy] = useState(false);
  const [cagnotte, setCagnotte] = useState(item.cagnotte_balance_eur ?? 0);
  const [walletBal, setWalletBal] = useState(item.wallet_balance_eur ?? 0);
  const [clientCredit, setClientCredit] = useState(item.balance_eur ?? 0);

  const loadLedger = useCallback(async () => {
    setLoadingLedger(true);
    try {
      const { data } = await api.get(`/admin/price/users/${item.user_id}/wallet/ledger?limit=20`);
      setLedger(Array.isArray(data?.entries) ? data.entries : []);
    } catch (_e) {
      setLedger([]);
    } finally {
      setLoadingLedger(false);
    }
  }, [item.user_id]);

  useEffect(() => { loadLedger(); }, [loadLedger]);

  const adjust = async (signMultiplier) => {
    const amt = Number(amount);
    if (!amt || isNaN(amt) || amt <= 0) {
      toast.error("Montant invalide");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        amount_eur: signMultiplier * amt,
        note: note || undefined,
      };
      if (isDriver) payload.balance_kind = balanceKind;
      const { data } = await api.post(`/admin/price/users/${item.user_id}/wallet/adjust`, payload);
      if (isDriver) {
        if (data.balance_kind === "cagnotte") setCagnotte(data.new_balance);
        else setWalletBal(data.new_balance);
      } else {
        setClientCredit(data.new_balance);
      }
      setAmount("");
      setNote("");
      const label = isDriver ? (balanceKind === "cagnotte" ? "cagnotte" : "portefeuille") : "crédit";
      toast.success(
        signMultiplier > 0
          ? `+${amt.toFixed(2)}€ crédités sur ${label} (nouveau : ${fmtEur(data.new_balance)})`
          : `-${amt.toFixed(2)}€ débités de ${label} (nouveau : ${fmtEur(data.new_balance)})`
      );
      await loadLedger();
      if (onUpdated) {
        onUpdated({
          ...item,
          cagnotte_balance_eur: data.balance_kind === "cagnotte" ? data.new_balance : (isDriver ? cagnotte : item.cagnotte_balance_eur),
          wallet_balance_eur: data.balance_kind === "wallet" ? data.new_balance : (isDriver ? walletBal : item.wallet_balance_eur),
          balance_eur: !isDriver ? data.new_balance : (Number(data.balance_kind === "cagnotte" ? data.new_balance : cagnotte) + Number(data.balance_kind === "wallet" ? data.new_balance : walletBal)),
        });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur ajustement");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-center sm:justify-center bg-black/50 backdrop-blur-sm"
      data-testid="wallet-detail-modal"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl border-2 border-black shadow-[0_8px_0_0_#000] max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b-2 border-black bg-[#FFE5E5]">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-[#FF3B3B] border-2 border-black grid place-items-center shadow-[0_3px_0_0_#000]">
              <Wallet className="w-4 h-4 text-white" strokeWidth={3} />
            </div>
            <div className="min-w-0">
              <div className="font-[Outfit] text-[15px] font-black leading-none truncate max-w-[220px]">
                {item.name || item.email}
              </div>
              <div className="text-[11px] font-bold text-neutral-600 uppercase mt-1">
                {isDriver ? "Covoitureur" : "Passager"} · {item.email}
              </div>
            </div>
          </div>
          <button
            type="button"
            data-testid="wallet-detail-close"
            onClick={onClose}
            className="w-9 h-9 rounded-xl border-2 border-black grid place-items-center bg-white active:translate-y-[2px]"
          >
            <X className="w-4 h-4" strokeWidth={3} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Soldes actuels */}
          {isDriver ? (
            <div className="p-5 border-b-2 border-black bg-white grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setBalanceKind("cagnotte")}
                className={
                  "rounded-2xl p-3 text-left transition-all " +
                  (balanceKind === "cagnotte"
                    ? "bg-primary text-primary-foreground border-2 border-black shadow-[0_3px_0_0_#000]"
                    : "bg-neutral-100 border-2 border-transparent")
                }
                data-testid="wallet-kind-cagnotte"
              >
                <div className="text-[9px] font-black uppercase tracking-widest opacity-80">
                  🐷 Cagnotte
                </div>
                <div className="mt-0.5 font-[Outfit] text-[22px] font-black tabular-nums leading-none">
                  {fmtEur(cagnotte)}
                </div>
                <div className="mt-1 text-[9px] font-bold uppercase opacity-70">
                  Non retirable
                </div>
              </button>
              <button
                type="button"
                onClick={() => setBalanceKind("wallet")}
                className={
                  "rounded-2xl p-3 text-left transition-all " +
                  (balanceKind === "wallet"
                    ? "bg-emerald-500 text-white border-2 border-black shadow-[0_3px_0_0_#000]"
                    : "bg-neutral-100 border-2 border-transparent")
                }
                data-testid="wallet-kind-wallet"
              >
                <div className="text-[9px] font-black uppercase tracking-widest opacity-80">
                  💳 Portefeuille
                </div>
                <div className="mt-0.5 font-[Outfit] text-[22px] font-black tabular-nums leading-none">
                  {fmtEur(walletBal)}
                </div>
                <div className="mt-1 text-[9px] font-bold uppercase opacity-70">
                  Retirable
                </div>
              </button>
            </div>
          ) : (
            <div className="p-5 border-b-2 border-black bg-white">
              <div className="text-[10px] font-black uppercase tracking-widest text-neutral-500">
                Crédit parrainage
              </div>
              <div className="mt-1 font-[Outfit] text-[36px] font-black tabular-nums leading-none">
                {fmtEur(clientCredit)}
              </div>
            </div>
          )}

          {/* Ajustement rapide */}
          <div className="p-5 border-b-2 border-black bg-neutral-50">
            <div className="text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-2">
              Ajuster le solde
            </div>
            <div className="grid gap-2">
              <div className="relative">
                <Euro className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" strokeWidth={3} />
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="Montant (ex : 5.00)"
                  className="w-full rounded-xl border-2 border-black bg-white pl-9 pr-3 py-2.5 text-[15px] font-[Outfit] font-black focus:outline-none focus:ring-0"
                  data-testid="wallet-adjust-amount"
                />
              </div>
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Note (optionnelle, ex : geste commercial)"
                maxLength={200}
                className="w-full rounded-xl border-2 border-black bg-white px-3 py-2 text-[13px] font-medium focus:outline-none focus:ring-0"
                data-testid="wallet-adjust-note"
              />
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => adjust(-1)}
                  className="flex items-center justify-center gap-1.5 rounded-xl border-2 border-black bg-white px-3 py-2.5 font-[Outfit] text-[13px] font-black uppercase tracking-wide shadow-[0_3px_0_0_#000] active:translate-y-[2px] active:shadow-[0_1px_0_0_#000] disabled:opacity-40"
                  data-testid="wallet-adjust-debit"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Minus className="h-4 w-4" strokeWidth={3} />}
                  Débit
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => adjust(+1)}
                  className="flex items-center justify-center gap-1.5 rounded-xl border-2 border-black bg-[#FF3B3B] text-white px-3 py-2.5 font-[Outfit] text-[13px] font-black uppercase tracking-wide shadow-[0_3px_0_0_#000] active:translate-y-[2px] active:shadow-[0_1px_0_0_#000] disabled:opacity-40"
                  data-testid="wallet-adjust-credit"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" strokeWidth={3} />}
                  Crédit
                </button>
              </div>
            </div>
          </div>

          {/* Historique */}
          <div className="p-5">
            <div className="text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-3">
              Historique ({ledger.length})
            </div>
            {loadingLedger ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-[#FF3B3B]" />
              </div>
            ) : ledger.length === 0 ? (
              <div className="text-center py-6 text-[12px] text-neutral-500 font-medium">
                Aucune transaction pour cet utilisateur.
              </div>
            ) : (
              <div className="space-y-1.5">
                {ledger.map((entry) => {
                  const amt = Number(entry.amount_eur || 0);
                  const positive = amt > 0;
                  return (
                    <div
                      key={entry.id || `${entry.created_at}-${amt}`}
                      className="flex items-center gap-2 rounded-xl border border-neutral-200 bg-white p-2"
                      data-testid={`wallet-ledger-${entry.id?.slice(0, 8)}`}
                    >
                      <div className={
                        "h-7 w-7 flex-none rounded-lg grid place-items-center " +
                        (positive ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600")
                      }>
                        {positive
                          ? <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={3} />
                          : <ArrowDownLeft className="h-3.5 w-3.5" strokeWidth={3} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] font-black uppercase tracking-wide truncate">
                          {entry.kind === "admin_adjustment" ? "Ajustement admin"
                            : entry.kind === "referral" ? "Parrainage"
                            : entry.kind === "commission" ? "Commission"
                            : entry.kind === "topup" ? "Recharge"
                            : (entry.kind || "Transaction")}
                        </div>
                        {(entry.note || entry.admin_email) && (
                          <div className="text-[10px] text-neutral-500 truncate">
                            {entry.note || ""}{entry.note && entry.admin_email ? " · " : ""}{entry.admin_email || ""}
                          </div>
                        )}
                        <div className="text-[10px] text-neutral-400">
                          <Clock className="h-2.5 w-2.5 inline mr-0.5" strokeWidth={3} />
                          {fmtDate(entry.created_at)}
                        </div>
                      </div>
                      <div className={
                        "font-[Outfit] font-black text-[13px] tabular-nums " +
                        (positive ? "text-emerald-600" : "text-rose-600")
                      }>
                        {positive ? "+" : ""}{fmtEur(amt)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function WalletsTab() {
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState(""); // "" | "driver" | "client"
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({ drivers_wallet_eur: 0, drivers_cagnotte_eur: 0, clients_credit_eur: 0, count: 0 });
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (roleFilter) params.set("role", roleFilter);
      params.set("limit", "200");
      const { data } = await api.get(`/admin/price/wallets?${params.toString()}`);
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotals(data?.totals || { drivers_wallet_eur: 0, drivers_cagnotte_eur: 0, clients_credit_eur: 0, count: 0 });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossible de charger les portefeuilles");
    } finally {
      setLoading(false);
    }
  }, [q, roleFilter]);

  useEffect(() => { load(); }, [load]);

  const onUpdated = (updated) => {
    setItems((prev) => prev.map((it) => it.user_id === updated.user_id ? updated : it));
  };

  return (
    <div className="space-y-3" data-testid="admin-wallets-tab">
      {/* Header */}
      <div className="rounded-2xl border-2 border-black bg-white p-4 shadow-[0_3px_0_0_#000]">
        <div className="flex items-center gap-2 mb-2">
          <Wallet className="w-4 h-4" strokeWidth={3} />
          <h2 className="font-[Outfit] font-black text-[15px]">Portefeuilles utilisateurs</h2>
        </div>
        <p className="text-[12px] text-neutral-600 mb-3">
          Vue globale des soldes covoitureurs + passagers. Clique sur un profil pour créditer/débiter.
        </p>
        <div className="grid gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-neutral-400" strokeWidth={3} />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher par email, nom, ID…"
              className="w-full rounded-xl border-2 border-black bg-white pl-8 pr-3 py-2 text-[13px] font-medium focus:outline-none"
              data-testid="admin-wallets-search"
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="w-full rounded-xl border-2 border-black bg-white px-2 py-1.5 text-[12px] font-bold"
            data-testid="admin-wallets-role"
          >
            <option value="">Tous (covoitureurs + passagers)</option>
            <option value="driver">Covoitureurs uniquement</option>
            <option value="client">Passagers uniquement</option>
          </select>
        </div>

        {/* Totaux */}
        <div className="mt-3 pt-3 border-t border-neutral-200 grid grid-cols-4 gap-2 text-center">
          <div>
            <div className="text-[10px] font-bold text-neutral-500 uppercase">🐷 Cagnottes</div>
            <div className="font-[Outfit] font-black text-[13px]">{fmtEur(totals.drivers_cagnotte_eur)}</div>
          </div>
          <div>
            <div className="text-[10px] font-bold text-neutral-500 uppercase">💳 Wallets</div>
            <div className="font-[Outfit] font-black text-[13px]">{fmtEur(totals.drivers_wallet_eur)}</div>
          </div>
          <div>
            <div className="text-[10px] font-bold text-neutral-500 uppercase">Crédit pass.</div>
            <div className="font-[Outfit] font-black text-[13px]">{fmtEur(totals.clients_credit_eur)}</div>
          </div>
          <div>
            <div className="text-[10px] font-bold text-neutral-500 uppercase">Users</div>
            <div className="font-[Outfit] font-black text-[15px]">{totals.count}</div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-[#FF3B3B]" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-10 rounded-2xl border-2 border-dashed border-neutral-300 bg-white">
          <Wallet className="w-8 h-8 text-neutral-400 mx-auto mb-2" />
          <p className="text-[13px] text-neutral-600 font-bold">Aucun résultat.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((it) => (
            <WalletRow key={`${it.role}-${it.user_id}`} item={it} onOpen={setSelected} />
          ))}
        </div>
      )}

      {selected && (
        <WalletDetailModal
          item={selected}
          onClose={() => setSelected(null)}
          onUpdated={onUpdated}
        />
      )}
    </div>
  );
}
